/**
 * Run `task` while holding a cross-tab mutex.
 * Chrome (including Salesforce CTI iframes on the same origin) uses the Web Locks API,
 * so a crashed tab cannot leave the lock stuck. Other tabs wait, then run one at a time.
 */
export function runWithCrossTabLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request(name, () => task());
  }
  return withLocalStorageLock(name, task);
}

type LockRecord = { owner: string; until: number };

function withLocalStorageLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const key = `rp.lock.${name}`;
  const owner = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const read = (): LockRecord | null => {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  };

  const tryAcquire = (): boolean => {
    const now = Date.now();
    const current = read();
    if (current && current.until > now && current.owner !== owner) {
      return false;
    }
    try {
      localStorage.setItem(key, JSON.stringify({ owner, until: now + 20_000 }));
    } catch {
      return false;
    }
    return read()?.owner === owner;
  };

  return new Promise((resolve, reject) => {
    const attempt = (tries: number) => {
      if (!tryAcquire()) {
        if (tries > 400) {
          reject(new Error(`Timed out waiting for cross-tab lock ${name}`));
          return;
        }
        setTimeout(() => attempt(tries + 1), 50);
        return;
      }

      const refresh = setInterval(() => {
        try {
          localStorage.setItem(key, JSON.stringify({ owner, until: Date.now() + 20_000 }));
        } catch {
          /* ignore quota / private mode */
        }
      }, 5_000);

      task().then(resolve, reject).finally(() => {
        clearInterval(refresh);
        if (read()?.owner === owner) {
          try {
            localStorage.removeItem(key);
          } catch {
            /* ignore */
          }
        }
      });
    };
    attempt(0);
  });
}
